import { useInfiniteQuery } from "@tanstack/react-query";
import {
  BlockView,
  ModeratorReportView,
  MyBlocksResponse,
  PersonReportResponse,
  ReportQueueResponse,
  type PersonReportRequest,
  type PersonReportStatus,
  type ReportDecisionRequest,
} from "@townsy/contracts";
import { API_BASE, apiFetch, isApiError } from "./client";

/** People safety (ADR-0013): reports, the moderator queue, and an account's mutes. */
export const safetyEndpoints = {
  reports: `${API_BASE}/reports`,
  reviewReports: `${API_BASE}/review/reports`,
  reviewDecision: (reportId: string) => `${API_BASE}/review/reports/${encodeURIComponent(reportId)}/decision`,
  blocks: `${API_BASE}/me/blocks`,
  block: (blockId: string) => `${API_BASE}/me/blocks/${encodeURIComponent(blockId)}`,
};

export const safetyKeys = { reports: ["review", "reports"] as const };

export function postPersonReport(body: PersonReportRequest): Promise<PersonReportResponse> {
  return apiFetch(safetyEndpoints.reports, { method: "POST", body, schema: PersonReportResponse });
}

export function fetchMyBlocks(signal?: AbortSignal): Promise<MyBlocksResponse> {
  return apiFetch(safetyEndpoints.blocks, { schema: MyBlocksResponse, signal });
}

export function postBlock(actorId: string): Promise<BlockView> {
  return apiFetch(safetyEndpoints.blocks, { method: "POST", body: { actorId }, schema: BlockView });
}

export async function deleteBlock(blockId: string): Promise<void> {
  await apiFetch(safetyEndpoints.block(blockId), { method: "DELETE" });
}

export function fetchReportQueue(cursor: string | null, status: PersonReportStatus): Promise<ReportQueueResponse> {
  const q = new URLSearchParams({ status, limit: "20" });
  if (cursor) q.set("cursor", cursor);
  return apiFetch(`${safetyEndpoints.reviewReports}?${q.toString()}`, { schema: ReportQueueResponse });
}

export function postReportDecision(reportId: string, body: ReportDecisionRequest): Promise<ModeratorReportView> {
  return apiFetch(safetyEndpoints.reviewDecision(reportId), { method: "POST", body, schema: ModeratorReportView });
}

export function useReportQueueQuery(status: PersonReportStatus) {
  return useInfiniteQuery({
    queryKey: [...safetyKeys.reports, status],
    queryFn: ({ pageParam }) => fetchReportQueue(pageParam, status),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    retry: (count, err) => !(isApiError(err) && (err.status === 401 || err.status === 403)) && count < 2,
  });
}
